import type { MountedExperiment } from '@learnforge/demo-kit';
import {
  detectHorizontalAsymptotes,
  detectVerticalAsymptotes,
  evaluate,
  findExtrema,
  findZerosBisection,
  formalDerivative,
  parse,
  sampleCurve,
  type ExprNode,
  type Extremum,
  type HorizontalAsymptote,
  type VerticalAsymptote,
} from '../func';
import { createActionBar } from '../action-bar';
import { createFunctionGraph, type FunctionGraphHandle } from '../render/function-graph';
import {
  createCartesianViewport,
  createFormulaLegend,
  createSvgEl,
  type CartesianViewportView,
  type FormulaLegendEntry,
} from '@learnforge/demo-render';
import { stringify } from '@learnforge/demo-math/expr';
import type { FuncContext } from '../types';

export interface GatheredFeatures {
  zeros: number[];
  extrema: Extremum[];
  verticalAsymptotes: VerticalAsymptote[];
  horizontalAsymptotes: HorizontalAsymptote[];
  yIntercept: number | null;
}

export function gatherFeatures(node: ExprNode, a: number, b: number): GatheredFeatures {
  const yI = evaluate(node, 0);
  return {
    zeros: findZerosBisection(node, a, b),
    extrema: findExtrema(node, a, b),
    verticalAsymptotes: detectVerticalAsymptotes(node, a, b),
    horizontalAsymptotes: detectHorizontalAsymptotes(node),
    yIntercept: Number.isFinite(yI) ? yI : null,
  };
}

type ToggleKey = 'zeros' | 'extrema' | 'va' | 'ha' | 'yInt';

export function mountBehaviorAndFeatures(ctx: FuncContext): MountedExperiment {
  let fSrc = '(x^2 - 1)/(x - 2)';
  let showDerivative = false;
  let toggles: Record<ToggleKey, boolean> = {
    zeros: false,
    extrema: false,
    va: false,
    ha: false,
    yInt: false,
  };

  const svg = createSvgEl('svg', { viewBox: '0 0 800 460', class: 'fn-stage' });
  ctx.root.appendChild(svg);
  const stageG = createSvgEl('g');
  svg.appendChild(stageG);
  const legend = createFormulaLegend(ctx.root);
  let view: CartesianViewportView = { xDomain: [-10, 10], yDomain: [-10, 10], zoom: 1 };

  let graphHandle: FunctionGraphHandle | null = null;
  function redraw(): void {
    const node = parse(fSrc).node;
    const sampleCount = Math.max(320, Math.round(800 * (globalThis.devicePixelRatio || 1)));
    const sample = sampleCurve(node, view.xDomain[0], view.xDomain[1], sampleCount);
    const derivativeNode = showDerivative ? formalDerivative(node) : null;
    const derivSample = derivativeNode
      ? sampleCurve(derivativeNode, view.xDomain[0], view.xDomain[1], sampleCount)
      : null;
    const features = gatherFeatures(node, view.xDomain[0], view.xDomain[1]);
    const featureList = [
      ...(toggles.zeros
        ? features.zeros.map((x) => ({
            x,
            y: 0,
            labelKey: 'feature.zero',
            className: 'fn-feature-zero',
          }))
        : []),
      ...(toggles.extrema
        ? features.extrema.map((e) => ({
            x: e.x,
            y: e.y,
            labelKey: e.kind === 'min' ? 'feature.extremum-min' : 'feature.extremum-max',
            className: `fn-feature-extremum-${e.kind}`,
          }))
        : []),
      ...(toggles.yInt && features.yIntercept !== null
        ? [
            {
              x: 0,
              y: features.yIntercept,
              labelKey: 'feature.y-intercept',
              className: 'fn-feature-yint',
            },
          ]
        : []),
    ];

    const asymptotes = [
      ...(toggles.va
        ? features.verticalAsymptotes.map((v) => ({ kind: 'vertical' as const, value: v.x }))
        : []),
      ...(toggles.ha
        ? features.horizontalAsymptotes.map((h) => ({ kind: 'horizontal' as const, value: h.y }))
        : []),
    ];
    const options = {
      xDomain: view.xDomain,
      yDomain: view.yDomain,
      width: 800,
      height: 460,
      curves: [
        { sample, className: 'fn-curve-primary', label: 'f(x)' },
        ...(derivSample
          ? [{ sample: derivSample, className: 'fn-curve-tertiary', label: "f'(x)" }]
          : []),
      ],
      discontinuities: features.verticalAsymptotes.map((asymptote) => asymptote.x),
      asymptotes,
      features: featureList,
      i18n: ctx.i18n,
    };
    if (graphHandle) graphHandle.update(options);
    else graphHandle = createFunctionGraph(stageG, options);
    const entries: FormulaLegendEntry[] = [
      { id: 'function', formula: `f(x) = ${fSrc}`, color: 'var(--accent)' },
      ...(derivativeNode
        ? [
            {
              id: 'derivative',
              formula: `f'(x) = ${stringify(derivativeNode)}`,
              color: 'var(--ink)',
            },
          ]
        : []),
      ...asymptotes.map((asymptote, index) => ({
        id: `asymptote-${asymptote.kind}-${index}`,
        formula: `${asymptote.kind === 'vertical' ? 'x' : 'y'} = ${asymptote.value.toFixed(2).replace(/\.00$/, '')}`,
        color: 'var(--ink-soft)',
        lineStyle: 'dashed' as const,
      })),
    ];
    legend.setEntries(entries);
  }

  const fRow = document.createElement('div');
  fRow.className = 'fn-panel-row';
  const fLabel = document.createElement('label');
  fLabel.textContent = ctx.i18n.t('param.f.label');
  const fInput = document.createElement('input');
  fInput.type = 'text';
  fInput.value = fSrc;
  fInput.addEventListener('change', () => {
    try {
      parse(fInput.value);
      fSrc = fInput.value;
      fInput.classList.remove('fn-input-error');
      redraw();
    } catch {
      fInput.classList.add('fn-input-error');
    }
  });
  fRow.appendChild(fLabel);
  fRow.appendChild(fInput);
  ctx.panel.appendChild(fRow);

  const featureSeparator = document.createElement('div');
  featureSeparator.className = 'fn-section-separator';
  ctx.panel.appendChild(featureSeparator);

  const toggleDefs: { key: ToggleKey; label: string }[] = [
    { key: 'zeros', label: ctx.i18n.t('feature.zero') },
    { key: 'extrema', label: ctx.i18n.t('feature.extrema') },
    { key: 'va', label: ctx.i18n.t('feature.vertical-asymptote') },
    { key: 'ha', label: ctx.i18n.t('feature.horizontal-asymptote') },
    { key: 'yInt', label: ctx.i18n.t('feature.y-intercept') },
  ];
  const toggleCheckboxes = {} as Record<ToggleKey, HTMLInputElement>;
  for (const def of toggleDefs) {
    const row = document.createElement('div');
    row.className = 'toggle-row fn-feature-toggle';
    const l = document.createElement('label');
    l.className = 'toggle-lbl';
    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.checked = toggles[def.key];
    cb.addEventListener('change', () => {
      toggles[def.key] = cb.checked;
      redraw();
    });
    toggleCheckboxes[def.key] = cb;
    const text = document.createElement('span');
    text.className = 'toggle-text';
    text.textContent = def.label;
    l.append(cb, text);
    row.appendChild(l);
    ctx.panel.appendChild(row);
  }

  const derivativeSeparator = document.createElement('div');
  derivativeSeparator.className = 'fn-section-separator';
  ctx.panel.appendChild(derivativeSeparator);

  const derivRow = document.createElement('div');
  derivRow.className = 'toggle-row fn-feature-toggle';
  const derivL = document.createElement('label');
  derivL.className = 'toggle-lbl';
  const derivCb = document.createElement('input');
  derivCb.type = 'checkbox';
  derivCb.checked = showDerivative;
  derivCb.addEventListener('change', () => {
    showDerivative = derivCb.checked;
    redraw();
  });
  const derivText = document.createElement('span');
  derivText.className = 'toggle-text';
  derivText.textContent = ctx.i18n.t('param.show-derivative.label');
  derivL.append(derivCb, derivText);
  derivRow.appendChild(derivL);
  ctx.panel.appendChild(derivRow);

  redraw();
  const viewport = createCartesianViewport(svg, {
    base: { xDomain: [-10, 10], yDomain: [-10, 10] },
    plotRect: { x: 0, y: 0, width: 800, height: 460 },
    onChange(next) {
      view = next;
      redraw();
    },
  });

  createActionBar(
    ctx.actionBar,
    [
      {
        id: 'auto',
        labelKey: ctx.i18n.t('btn.auto-find'),
        onClick: () => {
          toggles = { zeros: true, extrema: true, va: true, ha: true, yInt: true };
          for (const key of Object.keys(toggleCheckboxes) as ToggleKey[]) {
            toggleCheckboxes[key].checked = true;
          }
          redraw();
        },
      },
      {
        id: 'clear',
        labelKey: ctx.i18n.t('btn.clear-all'),
        onClick: () => {
          toggles = { zeros: false, extrema: false, va: false, ha: false, yInt: false };
          showDerivative = false;
          for (const key of Object.keys(toggleCheckboxes) as ToggleKey[]) {
            toggleCheckboxes[key].checked = false;
          }
          derivCb.checked = false;
          redraw();
        },
      },
    ],
    { viewReset: { labelKey: ctx.i18n.t('btn.reset-view'), onResetView: () => viewport.reset() } },
  );
  return {
    dispose() {
      graphHandle?.destroy();
      viewport.dispose();
      legend.destroy();
      svg.remove();
      ctx.panel.replaceChildren();
      ctx.actionBar.replaceChildren();
    },
  };
}
